import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input, Select, Spin, message, Modal } from 'antd';
import {
  useParams,
  useNavigate,
  UNSAFE_NavigationContext,
  useSearchParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import FlowJSONEditor from './flowJSONEditor';
import WhatsAppFlowPreview from '../preview/WhatsAppFlowPreview';
import {
  flowsGet,
  updateFlow,
  flowsAssets,
  publishFlow,
  updateFlowFieldMappings,
} from '../../../../api/Api';
import { FLOW_CATEGORY_OPTIONS } from '../constants';
import NotificationContext from '../../../../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../../../../components/commonComponents/gxAntdToast/toastUtils';
import FullArrow from '../../../../../../../src/assets/customSVG/FullArrow';
import Loader from '../../../../../../components/commonComponents/Loader/Index';

const NAME_CHAR_LIMIT = 200;

const formatCategoryLabel = (value) => {
  if (!value) return '';

  return value
    .toString()
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) =>
      word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '',
    )
    .join(' ');
};

const calculateCategoriesKey = (values) => {
  if (!Array.isArray(values) || values.length === 0) return '';
  return [...values].sort().join('|');
};

const DIGIT_TO_ALPHA = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const alphaSequence = (value) => {
  let number = Number(value);
  if (!Number.isFinite(number) || number < 1) {
    return 'A';
  }

  let result = '';
  let current = Math.floor(number);

  while (current > 0) {
    const remainder = (current - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    current = Math.floor((current - 1) / 26);
  }

  return result || 'A';
};

const sanitizeIdBase = (rawId) => {
  if (!rawId) return '';

  let base = String(rawId).toUpperCase();
  base = base.replace(/[^A-Z0-9_]/g, '_');
  base = base.replace(/\d/g, (digit) => DIGIT_TO_ALPHA[Number(digit)]);
  base = base.replace(/_{2,}/g, '_');
  base = base.replace(/^_+/, '');
  base = base.replace(/_+$/, '');

  if (!base) {
    return '';
  }

  if (!/^[A-Z]/.test(base)) {
    base = `S_${base}`;
  }

  base = base.replace(/[^A-Z_]/g, '_');
  base = base.replace(/_{2,}/g, '_');
  base = base.replace(/^_+/, '');
  base = base.replace(/_+$/, '');

  return base;
};

const ensureUniqueId = (base, used, counts) => {
  let candidate = base || 'SCREEN';

  if (!used.has(candidate)) {
    used.add(candidate);
    counts.set(base, 1);
    return candidate;
  }

  let suffixIndex = counts.get(base) || 1;
  let uniqueCandidate = candidate;

  while (used.has(uniqueCandidate)) {
    uniqueCandidate = `${candidate}_${alphaSequence(suffixIndex)}`;
    suffixIndex += 1;
  }

  counts.set(base, suffixIndex);
  used.add(uniqueCandidate);

  return uniqueCandidate;
};

const sanitizeAction = (action, idMap) => {
  if (!action || typeof action !== 'object') return action;
  const sanitizedAction = { ...action };

  if (
    sanitizedAction.name === 'navigate' &&
    sanitizedAction.next &&
    typeof sanitizedAction.next === 'object'
  ) {
    const nextTarget = sanitizedAction.next.name;
    if (typeof nextTarget === 'string') {
      const mappedTarget = idMap.get(nextTarget);
      if (mappedTarget) {
        sanitizedAction.next = {
          ...sanitizedAction.next,
          name: mappedTarget,
        };
      } else {
        const normalizedTarget = sanitizeIdBase(nextTarget);
        if (normalizedTarget) {
          sanitizedAction.next = {
            ...sanitizedAction.next,
            name: normalizedTarget,
          };
        }
      }
    }
  }

  if (sanitizedAction.payload && typeof sanitizedAction.payload === 'object') {
    try {
      sanitizedAction.payload = JSON.parse(
        JSON.stringify(sanitizedAction.payload),
      );
    } catch (error) {
      sanitizedAction.payload = { ...sanitizedAction.payload };
    }
  }

  return sanitizedAction;
};

const sanitizeComponent = (component, idMap) => {
  if (!component || typeof component !== 'object') return component;

  const sanitizedComponent = { ...component };

  if (sanitizedComponent.__meta) {
    delete sanitizedComponent.__meta;
  }

  if (sanitizedComponent['read-more-screen-id']) {
    delete sanitizedComponent['read-more-screen-id'];
  }

  if (Array.isArray(component.children)) {
    sanitizedComponent.children = component.children
      .map((child) => sanitizeComponent(child, idMap))
      .filter((child) => child !== undefined);
  }

  if (
    sanitizedComponent.type === 'Form' &&
    sanitizedComponent.visible !== undefined
  ) {
    delete sanitizedComponent.visible;
  }

  if (sanitizedComponent['on-click-action']) {
    sanitizedComponent['on-click-action'] = sanitizeAction(
      sanitizedComponent['on-click-action'],
      idMap,
    );
  }

  if (sanitizedComponent['on-select-action']) {
    sanitizedComponent['on-select-action'] = sanitizeAction(
      sanitizedComponent['on-select-action'],
      idMap,
    );
  }

  if (sanitizedComponent['on-unselect-action']) {
    sanitizedComponent['on-unselect-action'] = sanitizeAction(
      sanitizedComponent['on-unselect-action'],
      idMap,
    );
  }

  return sanitizedComponent;
};

const sanitizeLayout = (layout, idMap) => {
  if (!layout || typeof layout !== 'object') return layout;

  const sanitizedLayout = { ...layout };
  if (Array.isArray(layout.children)) {
    sanitizedLayout.children = layout.children
      .map((child) => sanitizeComponent(child, idMap))
      .filter((child) => child !== undefined);
  }

  return sanitizedLayout;
};

const sanitizeFlowJsonValue = (flow) => {
  if (!flow || typeof flow !== 'object') return flow;

  const result = { ...flow };
  const screens = Array.isArray(flow.screens) ? flow.screens : [];
  const usedIds = new Set();
  const duplicateCounts = new Map();
  const idMap = new Map();

  result.screens = screens.map((screen, index) => {
    if (!screen || typeof screen !== 'object') {
      return screen;
    }

    const rawId = typeof screen.id === 'string' ? screen.id : '';
    const fallbackBase = `SCREEN_${alphaSequence(index + 1)}`;
    const sanitizedBase =
      sanitizeIdBase(rawId) || sanitizeIdBase(fallbackBase) || fallbackBase;
    const uniqueId = ensureUniqueId(sanitizedBase, usedIds, duplicateCounts);

    if (typeof screen.id === 'string') {
      idMap.set(screen.id, uniqueId);
    }
    idMap.set(uniqueId, uniqueId);

    const sanitizedScreen = {
      ...screen,
      id: uniqueId,
    };

    if (sanitizedScreen.__meta) {
      delete sanitizedScreen.__meta;
    }

    if (screen.data && typeof screen.data === 'object') {
      try {
        sanitizedScreen.data = JSON.parse(JSON.stringify(screen.data));
      } catch (error) {
        sanitizedScreen.data = { ...screen.data };
      }
    } else if (screen.data === undefined) {
      delete sanitizedScreen.data;
    } else {
      sanitizedScreen.data = screen.data;
    }

    sanitizedScreen.layout = sanitizeLayout(screen.layout, idMap);

    return sanitizedScreen;
  });

  if (!result.version) {
    result.version = '7.2';
  }

  return result;
};

const collectReadMoreMetaFromScreens = (screens) => {
  const metaMap = new Map();
  if (!Array.isArray(screens)) {
    return metaMap;
  }

  const visitComponents = (components, parentScreenId) => {
    if (!Array.isArray(components)) return;

    components.forEach((component) => {
      if (!component || typeof component !== 'object') return;

      if (component.type === 'OptIn') {
        const action = component['on-click-action'];
        if (
          action?.name === 'navigate' &&
          action?.next?.type === 'screen' &&
          typeof action?.next?.name === 'string'
        ) {
          metaMap.set(action.next.name, {
            parentScreenId,
            parentOptInName: component.name || '',
          });
        }
      }

      if (Array.isArray(component.children) && component.children.length > 0) {
        visitComponents(component.children, parentScreenId);
      }
    });
  };

  screens.forEach((screen) => {
    if (!screen || typeof screen !== 'object') return;
    const layoutChildren = Array.isArray(screen.layout?.children)
      ? screen.layout.children
      : [];
    visitComponents(layoutChildren, screen.id);
  });

  return metaMap;
};

const applyReadMoreMetaToFlow = (flow) => {
  if (!flow || typeof flow !== 'object') return flow;

  const screens = Array.isArray(flow.screens) ? flow.screens : [];
  const metaMap = collectReadMoreMetaFromScreens(screens);

  const hydratedScreens = screens.map((screen) => {
    if (!screen || typeof screen !== 'object') return screen;
    const meta = metaMap.get(screen.id);
    if (!meta) {
      if (screen.__meta) {
        const { __meta, ...rest } = screen;
        return rest;
      }
      return screen;
    }

    return {
      ...screen,
      __meta: {
        isOptInReadMore: true,
        parentScreenId: meta.parentScreenId,
        parentOptInName: meta.parentOptInName,
      },
    };
  });

  return {
    ...flow,
    screens: hydratedScreens,
  };
};

const stringifyFlowJson = (value) => {
  if (!value) return '';
  try {
    const base =
      typeof value === 'string'
        ? JSON.parse(value)
        : JSON.parse(JSON.stringify(value));
    const sanitized = sanitizeFlowJsonValue(base);
    return JSON.stringify(sanitized);
  } catch (error) {
    console.error('Failed to serialize flow JSON:', error);
    return '';
  }
};

const parseFlowJson = (raw) => {
  if (!raw) return null;

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return applyReadMoreMetaToFlow(parsed);
    } catch (error) {
      console.error('Unable to parse flow_json string', error);
      return null;
    }
  }

  if (typeof raw === 'object') {
    try {
      const cloned = JSON.parse(JSON.stringify(raw));
      return applyReadMoreMetaToFlow(cloned);
    } catch (error) {
      console.error('Unable to clone flow_json object', error);
    }
    return applyReadMoreMetaToFlow(raw);
  }

  return null;
};

const extractFlowFieldIds = (flowJsonValue) => {
  const inputTypes = new Set([
    'TextInput',
    'TextArea',
    'DatePicker',
    'CheckboxGroup',
    'RadioButtonsGroup',
    'Dropdown',
    'OptIn',
  ]);

  const screens = Array.isArray(flowJsonValue?.screens)
    ? flowJsonValue.screens
    : [];

  const ids = [];

  screens.forEach((screen, screenIndex) => {
    if (!screen?.layout?.children) return;
    screen.layout.children.forEach((child) => {
      if (child?.type !== 'Form' || !Array.isArray(child.children)) return;

      child.children.forEach((formChild) => {
        if (inputTypes.has(formChild?.type) && formChild?.name) {
          // Include both prefixed and legacy IDs for validation
          ids.push(`${screenIndex}_${formChild.name}`);
          ids.push(formChild.name);
        }
      });
    });
  });

  return ids;
};

const buildFieldMappingsPayload = (flowJsonValue, mappings) => {
  const allowedFieldIds = new Set(extractFlowFieldIds(flowJsonValue));

  const nextMappings = {};
  Object.entries(mappings || {}).forEach(([fieldId, crmField]) => {
    if (fieldId && crmField && allowedFieldIds.has(fieldId)) {
      nextMappings[fieldId] = crmField;
    }
  });

  return {
    field_mappings: nextMappings,
    crm_sync_enabled: Object.keys(nextMappings).length > 0,
  };
};

const findDuplicateFieldNamesPerScreen = (flowJsonValue) => {
  const screens = Array.isArray(flowJsonValue?.screens)
    ? flowJsonValue.screens
    : [];

  const inputTypes = new Set([
    'TextInput',
    'TextArea',
    'DatePicker',
    'CheckboxGroup',
    'RadioButtonsGroup',
    'Dropdown',
    'OptIn',
  ]);

  const duplicates = [];

  screens.forEach((screen) => {
    if (!screen?.layout?.children) return;
    const counts = new Map();

    screen.layout.children.forEach((child) => {
      if (child?.type !== 'Form' || !Array.isArray(child.children)) return;

      child.children.forEach((formChild) => {
        if (inputTypes.has(formChild?.type) && formChild?.name) {
          const current = counts.get(formChild.name) || 0;
          counts.set(formChild.name, current + 1);
        }
      });
    });

    const dupNames = Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);

    if (dupNames.length) {
      duplicates.push({
        screenId: screen.id,
        names: dupNames,
      });
    }
  });

  return duplicates;
};

const useNavigationBlocker = (shouldBlock) => {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigator = navigationContext?.navigator;
  const [retryNavigation, setRetryNavigation] = useState(null);

  useEffect(() => {
    if (!shouldBlock) {
      setRetryNavigation(null);
      return undefined;
    }

    if (!navigator || typeof navigator.block !== 'function') {
      return undefined;
    }

    const unblock = navigator.block((tx) => {
      setRetryNavigation(() => () => {
        unblock();
        tx.retry();
      });
    });

    return () => {
      unblock();
      setRetryNavigation(null);
    };
  }, [navigator, shouldBlock]);

  const proceed = useCallback(() => {
    if (retryNavigation) {
      retryNavigation();
      setRetryNavigation(null);
    }
  }, [retryNavigation]);

  const reset = useCallback(() => {
    setRetryNavigation(null);
  }, []);

  return {
    isBlocked: Boolean(retryNavigation),
    proceed,
    reset,
  };
};

const EditFlow = ({ step }) => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { currentBrand } = useSelector((state) => state.app);
  const notification = useContext(NotificationContext);
  const [queryParams] = useSearchParams();

  const baseCategoryOptions = useMemo(
    () =>
      (FLOW_CATEGORY_OPTIONS || []).map((option) => ({
        label: option.name,
        value: option.code,
      })),
    [],
  );

  const categoryNameToCode = useMemo(() => {
    const map = new Map();
    (FLOW_CATEGORY_OPTIONS || []).forEach(({ name, code }) => {
      if (name && code) {
        map.set(name.toLowerCase(), code);
      }
    });
    return map;
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [flowDetails, setFlowDetails] = useState(null);
  const [flowJson, setFlowJson] = useState(null);
  const [activeScreenId, setActiveScreenId] = useState(null);
  const [name, setName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [editorKey, setEditorKey] = useState(() =>
    String(flowId || 'flow-editor'),
  );
  const [categoryOptions, setCategoryOptions] = useState(baseCategoryOptions);
  const [currentStep, setCurrentStep] = useState(queryParams.step || 2);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isSavingFlow, setIsSavingFlow] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [lastSavedMeta, setLastSavedMeta] = useState(null);
  const [lastSavedFlow, setLastSavedFlow] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});

  const brandId = currentBrand?.brand_id;
  const hasBrandSelected = Boolean(brandId);

  const trimmedName = useMemo(() => name.trim(), [name]);
  const categoriesKey = useMemo(
    () => calculateCategoriesKey(selectedCategories),
    [selectedCategories],
  );
  const currentFlowJsonString = useMemo(
    () => stringifyFlowJson(flowJson),
    [flowJson],
  );

  const lastUpdatedLabel = useMemo(() => {
    if (!flowDetails?.updated_at) return 'Not available';
    const date = new Date(flowDetails.updated_at);
    if (Number.isNaN(date.getTime())) return 'Not available';
    return date.toLocaleString();
  }, [flowDetails?.updated_at]);

  const isMetaDirty = useMemo(() => {
    if (!lastSavedMeta) return false;
    if (lastSavedMeta.name !== trimmedName) return true;
    if (lastSavedMeta.categoriesKey !== categoriesKey) return true;
    return false;
  }, [categoriesKey, lastSavedMeta, trimmedName]);

  const isFlowDirty = useMemo(() => {
    if (!lastSavedFlow) return false;
    return lastSavedFlow.flowJsonString !== currentFlowJsonString;
  }, [currentFlowJsonString, lastSavedFlow]);

  const isDirty = isMetaDirty || isFlowDirty;

  const {
    isBlocked: isNavigationBlocked,
    proceed: proceedNavigation,
    reset: resetNavigation,
  } = useNavigationBlocker(isDirty);
  const navigationModalRef = useRef(null);

  useEffect(() => {
    setCategoryOptions(baseCategoryOptions);
  }, [baseCategoryOptions]);

  const handleFieldMappingsChange = useCallback((updater) => {
    setFieldMappings((prev) => {
      const safePrev = prev || {};
      const nextValue =
        typeof updater === 'function' ? updater(safePrev) : updater;
      if (!nextValue) return {};
      return nextValue;
    });
  }, []);

  const extendCategoryOptions = useCallback((values) => {
    if (!values || values.length === 0) return;

    setCategoryOptions((prev) => {
      const next = [...prev];
      const seen = new Set(prev.map((option) => option.value));
      let mutated = false;

      values.forEach((value) => {
        if (!value || seen.has(value)) return;
        next.push({
          value,
          label: formatCategoryLabel(value),
        });
        seen.add(value);
        mutated = true;
      });

      return mutated ? next : prev;
    });
  }, []);

  const resolveCategoryValues = useCallback(
    (flow) => {
      if (!flow) return [];

      if (Array.isArray(flow.categories) && flow.categories.length > 0) {
        return flow.categories;
      }

      if (
        Array.isArray(flow.category_codes) &&
        flow.category_codes.length > 0
      ) {
        return flow.category_codes;
      }

      if (
        Array.isArray(flow.category_names) &&
        flow.category_names.length > 0
      ) {
        return flow.category_names.map((categoryName) => {
          const mapped = categoryNameToCode.get(
            String(categoryName).toLowerCase(),
          );
          return mapped || String(categoryName);
        });
      }

      return [];
    },
    [categoryNameToCode],
  );

  const fetchFlowDetails = useCallback(async () => {
    if (!flowId || !brandId) return;

    setIsLoading(true);
    setFetchError(null);

    try {
      const response = await flowsGet(flowId, brandId);
      const flowPayload =
        response?.data?.flow ||
        response?.data?.data ||
        response?.data ||
        response;

      if (!flowPayload) {
        throw new Error('Flow details were not found.');
      }

      const parsedFlowJson = parseFlowJson(flowPayload.flow_json);
      if (!parsedFlowJson) {
        message.warning(
          'Flow JSON is missing or invalid. Starting with an empty draft.',
        );
      }

      const categoriesFromResponse = resolveCategoryValues(flowPayload);
      extendCategoryOptions(categoriesFromResponse);

      setFlowDetails(flowPayload);
      setFlowJson(parsedFlowJson);
      setFieldMappings(flowPayload?.field_mappings || {});
      setName(flowPayload?.name ?? '');
      setSelectedCategories(categoriesFromResponse);
      setEditorKey(
        String(
          flowPayload?.flow_json_checksum ||
            flowPayload?.updated_at ||
            flowPayload?.id ||
            flowId,
        ),
      );
      setLastSavedMeta({
        name: (flowPayload?.name ?? '').trim(),
        categories: Array.isArray(categoriesFromResponse)
          ? [...categoriesFromResponse]
          : [],
        categoriesKey: calculateCategoriesKey(categoriesFromResponse),
      });
      setLastSavedFlow({
        flowJsonString: stringifyFlowJson(parsedFlowJson),
      });
      setCurrentStep(1);
    } catch (error) {
      console.error('Failed to fetch flow details:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load flow details. Please try again.';
      setFetchError(errorMessage);
      setFlowDetails(null);
      setFlowJson(null);
      setFieldMappings({});
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, extendCategoryOptions, flowId, resolveCategoryValues]);

  useEffect(() => {
    if (!flowId || !brandId) {
      setFlowDetails(null);
      setFlowJson(null);
      setSelectedCategories([]);
      setFetchError(null);
      setCurrentStep(1);
      setFieldMappings({});
      return;
    }

    fetchFlowDetails();
  }, [brandId, fetchFlowDetails, flowId]);

  const handleRetry = useCallback(() => {
    if (!brandId) {
      message.info('Please select a brand to load flow details.');
      return;
    }
    fetchFlowDetails();
  }, [brandId, fetchFlowDetails]);

  const handleNameChange = useCallback((event) => {
    const nextValue = event.target.value ?? '';
    setName(nextValue.slice(0, NAME_CHAR_LIMIT));
  }, []);

  const handleCategoriesChange = useCallback(
    (values) => {
      extendCategoryOptions(values);
      setSelectedCategories(values || []);
    },
    [extendCategoryOptions],
  );

  const handleFlowJsonChange = useCallback((updatedFlowJson) => {
    setFlowJson(updatedFlowJson);
  }, []);

  const handleActiveScreenChange = useCallback(({ screenId }) => {
    if (!screenId) return;
    setActiveScreenId((prev) => (prev === screenId ? prev : screenId));
  }, []);

  const handlePreviewScreenChange = useCallback((screenId) => {
    if (!screenId) return;
    setActiveScreenId((prev) => (prev === screenId ? prev : screenId));
  }, []);

  useEffect(() => {
    if (!isNavigationBlocked) {
      if (navigationModalRef.current) {
        navigationModalRef.current.destroy();
        navigationModalRef.current = null;
      }
      return undefined;
    }

    if (navigationModalRef.current) {
      return undefined;
    }

    const modalInstance = Modal.confirm({
      title: 'Leave this page?',
      content:
        'You have unsaved changes. If you leave now, your updates will be lost.',
      okText: 'Leave',
      okType: 'danger',
      cancelText: 'Stay',
      centered: true,
      onOk: () => {
        navigationModalRef.current = null;
        proceedNavigation();
      },
      onCancel: () => {
        navigationModalRef.current = null;
        resetNavigation();
      },
      maskClosable: true,
    });

    navigationModalRef.current = modalInstance;

    return () => {
      if (navigationModalRef.current) {
        navigationModalRef.current.destroy();
        navigationModalRef.current = null;
      }
    };
  }, [isNavigationBlocked, proceedNavigation, resetNavigation]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  const handleDiscardFlow = useCallback(() => {
    if (!lastSavedFlow) {
      message.info('No flow changes to discard.');
      return;
    }

    const nextFlowJson = (() => {
      if (!lastSavedFlow?.flowJsonString) return null;
      try {
        return JSON.parse(lastSavedFlow.flowJsonString);
      } catch (error) {
        console.error('Failed to parse saved flow JSON:', error);
        return null;
      }
    })();

    setFlowJson(nextFlowJson);
    setActiveScreenId(null);
    setEditorKey(
      String(
        `flow-editor-discard-${
          lastSavedFlow?.flowJsonString
            ? lastSavedFlow.flowJsonString.length
            : 'empty'
        }-${Date.now()}`,
      ),
    );
    message.info('Flow reverted to the last saved version.');
  }, [lastSavedFlow]);

  const handleSaveDetails = useCallback(
    async ({ silent = false } = {}) => {
      if (!flowId || !brandId) {
        if (!silent) {
          message.error('Flow or brand information is missing.');
        }
        return false;
      }

      if (!trimmedName) {
        if (!silent) {
          message.error('Flow name is required.');
        }
        return false;
      }

      if (!selectedCategories || selectedCategories.length === 0) {
        if (!silent) {
          message.error('Please select at least one category.');
        }
        return false;
      }

      if (!isMetaDirty) {
        if (!silent) {
          message.info('No changes to save.');
        }
        return true;
      }

      const metaPayload = {
        brand_id: brandId,
        name: trimmedName,
        categories: selectedCategories,
      };

      try {
        setIsSavingDetails(true);
        await updateFlow(flowId, metaPayload);

        setFlowDetails((prev) =>
          prev
            ? {
                ...prev,
                name: trimmedName,
                categories: selectedCategories,
                category_codes: selectedCategories,
                updated_at: new Date().toISOString(),
              }
            : prev,
        );

        setLastSavedMeta({
          name: trimmedName,
          categories: [...selectedCategories],
          categoriesKey,
        });

        if (!silent) {
          showSuccessToast(notification, 'Flow details updated successfully');
        }
        return true;
      } catch (error) {
        console.error('Failed to save flow details:', error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save flow details. Please try again.';
        if (!silent) {
          message.error(errorMessage);
        }
        return false;
      } finally {
        setIsSavingDetails(false);
      }
    },
    [
      brandId,
      categoriesKey,
      flowId,
      isMetaDirty,
      selectedCategories,
      trimmedName,
    ],
  );

  const syncFieldMappings = useCallback(
    async ({ silent = false } = {}) => {
      if (!flowId || !brandId) {
        if (!silent) {
          message.error('Flow or brand information is missing.');
        }
        return false;
      }

      const payload = buildFieldMappingsPayload(flowJson, fieldMappings);

      try {
        await updateFlowFieldMappings(flowId, brandId, payload);
        setFieldMappings(payload.field_mappings);
        setFlowDetails((prev) =>
          prev
            ? {
                ...prev,
                field_mappings: payload.field_mappings,
                crm_sync_enabled: payload.crm_sync_enabled,
              }
            : prev,
        );
        return true;
      } catch (error) {
        console.error('Failed to update CRM field mappings:', error);
        if (!silent) {
          message.error(
            error?.response?.data?.error ||
              error?.response?.data?.message ||
              'Failed to update CRM field mappings.',
          );
        }
        return false;
      }
    },
    [brandId, fieldMappings, flowId, flowJson],
  );

  const handleSaveFlow = useCallback(
    async ({ silent = false } = {}) => {
      if (!flowId || !brandId) {
        if (!silent) {
          message.error('Flow or brand information is missing.');
        }
        return false;
      }

      const duplicateScreens = findDuplicateFieldNamesPerScreen(flowJson);
      if (duplicateScreens.length) {
        const first = duplicateScreens[0];
        const nameList = first.names.join(', ');
        message.error(
          `Field names must be unique within a screen. Duplicate(s): ${nameList} in screen ${
            first.screenId || 'unknown'
          }.`,
        );
        return false;
      }

      if (!currentFlowJsonString) {
        if (!silent) {
          message.error(
            'Flow content is empty. Please configure the flow before saving.',
          );
        }
        return false;
      }

      try {
        setIsSavingFlow(true);

        if (isFlowDirty) {
          const assetsPayload = {
            flow_json: currentFlowJsonString,
          };

          await flowsAssets(flowId, brandId, assetsPayload);

          setFlowDetails((prev) =>
            prev
              ? {
                  ...prev,
                  flow_json: assetsPayload.flow_json,
                  updated_at: new Date().toISOString(),
                }
              : prev,
          );

          setLastSavedFlow({
            flowJsonString: currentFlowJsonString,
          });

          if (!silent) {
            message.success('Flow JSON updated successfully.');
          }
        } else if (!silent) {
          // message.info("No flow changes to save.");
        }

        const mappingsSynced = await syncFieldMappings({ silent });
        return mappingsSynced;
      } catch (error) {
        console.error('Failed to save flow JSON:', error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save flow JSON. Please try again.';
        if (!silent) {
          message.error(errorMessage);
        }
        return false;
      } finally {
        setIsSavingFlow(false);
      }
    },
    [brandId, currentFlowJsonString, flowId, isFlowDirty, syncFieldMappings],
  );

  const handlePublishFlow = useCallback(async () => {
    if (!flowId || !brandId) {
      message.error('Flow or brand information is missing.');
      return;
    }

    if (isPublishing) {
      message.info('Publishing is already in progress.');
      return;
    }

    const duplicateScreens = findDuplicateFieldNamesPerScreen(flowJson);
    if (duplicateScreens.length) {
      const first = duplicateScreens[0];
      const nameList = first.names.join(', ');
      message.error(
        `Field names must be unique within a screen. Duplicate(s): ${nameList} in screen ${
          first.screenId || 'unknown'
        }.`,
      );
      return;
    }

    let dismissSaving;
    let dismissPublishing;
    try {
      if (isMetaDirty || isFlowDirty) {
        dismissSaving = message.loading('Saving your latest changes…', 0);
        const detailsSaved = isMetaDirty
          ? await handleSaveDetails({ silent: true })
          : true;
        const flowSaved =
          detailsSaved && isFlowDirty
            ? await handleSaveFlow({ silent: true })
            : true;

        if (dismissSaving) {
          dismissSaving();
          dismissSaving = null;
        }

        if (!detailsSaved || !flowSaved) {
          message.error(
            "We couldn't save all changes. Please resolve the issues before publishing.",
          );
          return;
        }

        message.success('All changes saved successfully. Publishing now…');
      }

      const mappingsSynced = await syncFieldMappings({ silent: true });
      if (!mappingsSynced) {
        message.error('Failed to update CRM field mappings. Please try again.');
        return;
      }

      setIsPublishing(true);
      dismissPublishing = message.loading('Publishing flow…', 0);
      const response = await publishFlow(flowId, { brand_id: brandId });
      const publishedFlow =
        response?.data?.flow ||
        response?.data?.data ||
        response?.data ||
        response;

      setFlowDetails((prev) => {
        if (publishedFlow && typeof publishedFlow === 'object') {
          return {
            ...prev,
            ...publishedFlow,
            status: publishedFlow.status ?? prev?.status,
          };
        }
        return prev
          ? {
              ...prev,
              status: publishedFlow?.status ?? prev.status,
              updated_at: new Date().toISOString(),
            }
          : prev;
      });

      if (publishedFlow && typeof publishedFlow === 'object') {
        const nextName =
          typeof publishedFlow.name === 'string'
            ? publishedFlow.name
            : trimmedName;
        const publishedCategories = Array.isArray(publishedFlow.categories)
          ? publishedFlow.categories
          : Array.isArray(publishedFlow.category_codes)
          ? publishedFlow.category_codes
          : null;
        const nextCategories = publishedCategories ?? selectedCategories;

        if (typeof publishedFlow.name === 'string') {
          setName(publishedFlow.name);
        }
        if (publishedCategories) {
          setSelectedCategories(publishedCategories);
        }

        setLastSavedMeta({
          name: nextName,
          categories: [...nextCategories],
          categoriesKey: calculateCategoriesKey(nextCategories),
        });
      }

      if (publishedFlow?.flow_json) {
        const parsed = parseFlowJson(publishedFlow.flow_json);
        if (parsed) {
          setFlowJson(parsed);
          setLastSavedFlow({ flowJsonString: stringifyFlowJson(parsed) });
          setEditorKey(String(Date.now()));
        } else {
          setLastSavedFlow({ flowJsonString: currentFlowJsonString });
        }
      }

      if (dismissPublishing) {
        dismissPublishing();
        dismissPublishing = undefined;
      }
      message.success('Flow published successfully.');
      navigate('/user/channels/whatsapp/flows');
    } catch (error) {
      if (dismissSaving) {
        dismissSaving();
        dismissSaving = undefined;
      }
      if (dismissPublishing) {
        dismissPublishing();
        dismissPublishing = undefined;
      }
      console.error('Failed to publish flow:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to publish flow. Please try again.';
      message.error(errorMessage);
    } finally {
      setIsPublishing(false);
    }
  }, [
    brandId,
    calculateCategoriesKey,
    currentFlowJsonString,
    flowId,
    handleSaveDetails,
    handleSaveFlow,
    isFlowDirty,
    isMetaDirty,
    isPublishing,
    navigate,
    selectedCategories,
    syncFieldMappings,
    trimmedName,
  ]);

  const handleGoToNextStep = useCallback(() => {
    if (!trimmedName || selectedCategories.length === 0) {
      message.error('Please provide a name and at least one category.');
      return;
    }
    if (isMetaDirty) {
      message.warning('Save the flow details before continuing.');
      return;
    }
    if (isPublishing) {
      message.info('Please wait for the publish action to finish.');
      return;
    }
    setCurrentStep(2);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isMetaDirty, isPublishing, selectedCategories, trimmedName]);

  const handleGoToPreviousStep = useCallback(() => {
    setCurrentStep(1);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const isSaveDetailsDisabled =
    isSavingDetails ||
    isPublishing ||
    !trimmedName ||
    selectedCategories.length === 0 ||
    !isMetaDirty;
  const isSaveFlowDisabled =
    isSavingFlow || isPublishing || !currentFlowJsonString || !isFlowDirty;
  const isDiscardFlowDisabled = !isFlowDirty || isSavingFlow || isPublishing;
  const isPublishingDisabled =
    isPublishing ||
    isSavingDetails ||
    isSavingFlow ||
    isMetaDirty ||
    isFlowDirty ||
    !brandId ||
    !flowDetails;
  const canProceedToNext =
    !isMetaDirty &&
    !isSavingDetails &&
    !isPublishing &&
    trimmedName.length > 0 &&
    selectedCategories.length > 0;

  const nameLength = name?.length ?? 0;
  const flowIdentifier =
    flowDetails?.flow_id || flowDetails?.id || flowDetails?.uuid || flowId;
  const isBuilderStep = currentStep === 2;

  useEffect(() => {
    const screens = flowJson?.screens;
    if (!Array.isArray(screens) || screens.length === 0) {
      setActiveScreenId(null);
      return;
    }

    if (!activeScreenId) {
      setActiveScreenId(screens[0]?.id ?? null);
      return;
    }

    const exists = screens.some((screen) => screen.id === activeScreenId);
    if (!exists) {
      setActiveScreenId(screens[0]?.id ?? null);
    }
  }, [flowJson, activeScreenId]);

  return (
    <div
      className={`w-full ${
        isBuilderStep ? 'h-screen overflow-hidden' : 'min-h-screen'
      }`}
    >
      <div
        className={`mx-auto flex w-full flex-col gap-2 ${
          isBuilderStep ? 'h-full overflow-hidden' : 'h-full'
        }`}
      >
        {!flowId && (
          <div className="flex flex-1 items-center justify-center rounded-xl bg-white p-12 text-center text-gray-500">
            Flow identifier is missing from the URL.
          </div>
        )}

        {flowId && !hasBrandSelected && (
          <div className="flex flex-1 items-center justify-center rounded-xl bg-white p-12 text-center text-gray-500">
            Please select a brand to continue editing this flow.
          </div>
        )}

        {flowId && hasBrandSelected && isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Loader Width={20} Height={20} />
          </div>
        )}

        {flowId && hasBrandSelected && !isLoading && fetchError && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-xl bg-white p-12 text-center">
            <div className="text-lg font-semibold text-gray-800">
              Unable to load flow details
            </div>
            <div className="max-w-md text-sm text-gray-500">{fetchError}</div>
            <Button type="primary" onClick={handleRetry}>
              Try again
            </Button>
          </div>
        )}

        {flowId &&
          hasBrandSelected &&
          !isLoading &&
          !fetchError &&
          flowDetails &&
          (currentStep === 1 ? (
            <section className="w-full rounded-2xl bg-gradient-to-r from-[#dce7ff] via-white to-[#e8f5ff] p-[1.5px] shadow-sm">
              <div className="w-full rounded-2xl bg-white p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Step 1 of 2
                    </p>
                    <h1 className="mt-1 text-xl font-semibold text-gray-800">
                      Flow details
                    </h1>
                    <p className="text-sm text-gray-500">
                      Update the name and categories before configuring the
                      flow.
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <div>
                      Flow ID:{' '}
                      <span className="font-medium text-gray-700">
                        {flowIdentifier || '-'}
                      </span>
                    </div>
                    <div className="mt-1">
                      Status:{' '}
                      <span className="font-medium text-gray-700">
                        {flowDetails?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid gap-5 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label
                      className="block text-sm font-semibold text-gray-800"
                      htmlFor="flow-edit-name"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center rounded-lg border border-gray-200 px-3 py-1.5">
                      <Input
                        id="flow-edit-name"
                        bordered={false}
                        placeholder="Enter flow name"
                        value={name}
                        onChange={handleNameChange}
                        maxLength={NAME_CHAR_LIMIT}
                        className="!border-none"
                      />
                      <span className="ml-2 text-xs text-gray-400">
                        {nameLength}/{NAME_CHAR_LIMIT}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-semibold text-gray-800">
                      Categories <span className="text-red-500">*</span>
                    </label>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select categories"
                      className="w-full"
                      value={selectedCategories}
                      options={categoryOptions}
                      onChange={handleCategoriesChange}
                      maxTagCount="responsive"
                      size="large"
                      style={{
                        minHeight: 45,
                        borderRadius: 8,
                      }}
                      dropdownStyle={{
                        padding: 12,
                        borderRadius: 8,
                      }}
                    />
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                  <div className="text-xs text-gray-500">
                    Last updated:{' '}
                    <span className="font-medium text-gray-700">
                      {lastUpdatedLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      size="large"
                      type="default"
                      onClick={() => {
                        void handleSaveDetails();
                      }}
                      loading={isSavingDetails}
                      disabled={isSaveDetailsDisabled}
                      className="!border-gray-200 !text-gray-700 hover:!border-blue-200"
                    >
                      Save
                    </Button>
                    <Button
                      size="large"
                      type="primary"
                      className={`!bg-[var(--new-primary)] text-white px-4 py-1.5 font-medium rounded-md transition-colors ${
                        !canProceedToNext
                          ? 'opacity-50 cursor-not-allowed !text-white'
                          : 'hover:bg-[var(--new-primary-dark)]'
                      }`}
                      onClick={handleGoToNextStep}
                      disabled={!canProceedToNext}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <div
              className={
                isBuilderStep
                  ? 'flex h-full flex-col gap-2 overflow-hidden'
                  : ''
              }
            >
              <section className="w-full rounded-2xl bg-gradient-to-r from-[#dce7ff] via-white to-[#e8f5ff] p-[1.5px] shadow-sm">
                <div className="w-full rounded-2xl bg-white px-5 py-[12px]">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {/* <Button
                        type="default"
                        size="large"
                        className="!border-gray-200 !text-gray-600 hover:!border-blue-200"
                        onClick={handleGoToPreviousStep}
                      > */}
                      <div
                        className="size-8 flex items-center justify-center rotate-180 p-1.5 border-2 border-[#2D3036] rounded-full cursor-pointer"
                        onClick={handleGoToPreviousStep}
                      >
                        <FullArrow color="#2D3036" />
                      </div>
                      {/* </Button> */}
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Step 2 of 2
                        </p>
                        <h2 className="text-lg font-semibold text-gray-800">
                          Configure flow JSON
                        </h2>
                        <p className="text-sm text-gray-500">
                          Build the customer experience and preview each screen.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* <Button
                        size="large"
                        type="default"
                        danger
                        onClick={handleDiscardFlow}
                        disabled={isDiscardFlowDisabled}
                      >
                        Discard changes
                      </Button> */}
                      <Button
                        size="large"
                        type="default"
                        onClick={() => {
                          void handleSaveFlow();
                        }}
                        className={`!border-[var(--new-primary)] !text-[var(--new-primary)] hover:!border-[var(--new-primary)]
                        ${
                          isSavingFlow
                            ? 'opacity-50 cursor-not-allowed !text-[var(--new-primary)]'
                            : ''
                        }`}
                        loading={isSavingFlow}
                        disabled={isSavingFlow}
                      >
                        Save as draft
                      </Button>
                      <Button
                        size="large"
                        type="primary"
                        onClick={async () => {
                          if (isSavingFlow || isPublishing) return;

                          try {
                            if (isDirty) {
                              await handleSaveFlow();
                            }
                            await handlePublishFlow();
                          } catch (error) {
                            console.error('Error in publish flow:', error);
                          }
                        }}
                        loading={isPublishing}
                        disabled={
                          isPublishing ||
                          isSavingFlow ||
                          !brandId ||
                          !flowDetails
                        }
                        className={`!bg-[var(--new-primary)] text-white px-4 py-1.5 font-medium rounded-md transition-colors ${
                          isPublishing || isSavingFlow
                            ? 'opacity-50 cursor-not-allowed !text-white'
                            : 'hover:bg-[var(--new-primary-dark)]'
                        }`}
                      >
                        Save and publish
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
                    <span>
                      Flow Name:{' '}
                      <span className="font-medium text-gray-700">
                        {flowDetails?.name || '-'}
                      </span>
                    </span>
                    <span>
                      Flow ID:{' '}
                      <span className="font-medium text-gray-700">
                        {flowIdentifier || '-'}
                      </span>
                    </span>
                    <span>
                      Status:{' '}
                      <span className="font-medium text-gray-700">
                        {flowDetails?.status || 'Unknown'}
                      </span>
                    </span>
                    <span>
                      Last saved:{' '}
                      <span className="font-medium text-gray-700">
                        {lastUpdatedLabel}
                      </span>
                    </span>
                  </div>
                </div>
              </section>

              <div className="grid flex-1 min-h-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_360px]">
                <div className="flex min-h-0 flex-col gap-6">
                  <section className="flex h-full min-h-0 w-full rounded-2xl bg-gradient-to-b from-[#e8ecf7] to-[#dfe9f3] p-[1px] shadow-sm">
                    <div className="flex h-full min-h-0 w-full rounded-2xl bg-white overflow-hidden">
                      <FlowJSONEditor
                        key={editorKey}
                        initialFlowJSON={flowJson}
                        onFlowChange={handleFlowJsonChange}
                        onActiveScreenChange={handleActiveScreenChange}
                        activeScreenId={activeScreenId}
                        brandId={brandId}
                        flowId={flowId}
                        fieldMappings={fieldMappings}
                        onFieldMappingsChange={handleFieldMappingsChange}
                      />
                    </div>
                  </section>
                </div>

                <div className="lg:pl-2 lg:top-40 self-start">
                  <div className="rounded-2xl bg-white p-2 shadow-sm">
                    <WhatsAppFlowPreview
                      flow_json={flowJson}
                      selectedTemplate={editorKey}
                      instantUpdate
                      externalCurrentScreenId={activeScreenId}
                      onScreenChange={handlePreviewScreenChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default EditFlow;
